import { LocaleTypes } from '@/types/LocaleTypes'

import { ResourceFragment } from '@/generated/contentful'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getLocalizedFromEnglish } from '@/utils/locales/localized-routes/getLocalizedFromEnglish'

import { getResourcePageType } from './getResourcePageType'

export const SUPPORTED_LOCALES: {
	locale: Locale
	slugKey: keyof ResourceFragment
	statusKey: keyof ResourceFragment
	hrefLang: string
}[] = [
	{
		locale: LocaleTypes.en_US,
		slugKey: 'slug_en_US',
		statusKey: 'status_en_US',
		hrefLang: 'en-us'
	},
	{
		locale: LocaleTypes.en_AU,
		slugKey: 'slug_en_AU',
		statusKey: 'status_en_AU',
		hrefLang: 'en-au'
	},
	{
		locale: LocaleTypes.en_GB,
		slugKey: 'slug_en_GB',
		statusKey: 'status_en_GB',
		hrefLang: 'en-gb'
	},
	{
		locale: LocaleTypes.fr_FR,
		slugKey: 'slug_fr_FR',
		statusKey: 'status_fr_FR',
		hrefLang: 'fr-fr'
	},
	{
		locale: LocaleTypes.it_IT,
		slugKey: 'slug_it_IT',
		statusKey: 'status_it_IT',
		hrefLang: 'it-it'
	},
	{
		locale: LocaleTypes.de_DE,
		slugKey: 'slug_de_DE',
		statusKey: 'status_de_DE',
		hrefLang: 'de-de'
	},
	{
		locale: LocaleTypes.nl_NL,
		slugKey: 'slug_nl_NL',
		statusKey: 'status_nl_NL',
		hrefLang: 'nl-nl'
	},
	{
		locale: LocaleTypes.es_ES,
		slugKey: 'slug_es_ES',
		statusKey: 'status_es_ES',
		hrefLang: 'es-es'
	},
	{ locale: LocaleTypes.es_MX, slugKey: 'slug_es_MX', statusKey: 'status_es_MX', hrefLang: 'es-mx' }
]

export const buildLanguageAlternates = (
	resource: ResourceFragment
): Array<{ hrefLang: string; href: string }> => {
	const alternates = SUPPORTED_LOCALES.reduce((acc, { locale, slugKey, statusKey, hrefLang }) => {
		const localizedSlug = resource[slugKey]
		const isStatusEnabled = resource[statusKey]
		if (localizedSlug && isStatusEnabled) {
			acc.push({
				hrefLang,
				href: `${getBaseUrl(locale)}${getLocalizedFromEnglish(
					locale,
					`/resources/${getResourcePageType(resource.type)}/${localizedSlug}/`
				)}`
			})
		}
		return acc
	}, [] as { hrefLang: string; href: string }[])
	// Create Hreflang tags only if the content is enabled for more than one locale
	return alternates.length > 1 ? alternates : []
}

export const getXDefaultHref = (resource: ResourceFragment): string | null => {
	const prioritizedLocales = [LocaleTypes.en_US, LocaleTypes.en_GB, LocaleTypes.en_AU]

	const availableLocales = SUPPORTED_LOCALES.filter(({ slugKey, statusKey }) => {
		const slug = resource[slugKey]
		const isEnabled = resource[statusKey]
		return Boolean(slug && isEnabled)
	})

	if (availableLocales.length <= 1) {
		return null
	}

	for (const locale of prioritizedLocales) {
		const config = availableLocales.find((item) => item.locale === locale)
		if (!config) continue

		const slug = resource[config.slugKey]
		if (!slug) continue
		return `${getBaseUrl(locale)}${getLocalizedFromEnglish(
			locale as Locale,
			`/resources/${getResourcePageType(resource.type)}/${slug}/`
		)}`
	}

	// Final fallback: return null if nothing valid is found
	return null
}
